import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { mediaQueries } from 'plugins/device'
import {
  TextCallout1,
  IconMedalMulticolor,
  IconQuickResponseMulticolor,
  IconLeafMulticolor,
  PopoverCentered,
  TextCallout2,
  TextCaption2,
  Divider,
  IconWarning,
  colors,
} from '@blocket/blocket-ui'
import ProfileCard from 'components/sharedComponents/ProfileCard/ProfileCard'
import SustainabilityInfo from 'components/sharedComponents/SustainabilityInfo'
import settings from '../../RatingReviews/sharedUtils/settings'
import ReportUser from './ReportUser'

export const UserProfileSection = styled.div`
  position: relative;
  top: 24px;
  max-width: 980px;
  width: 100%;
  border-radius: 6px;
  background-color: ${colors.white100};
  border-radius: 16px 16px 0px 0px;
  display: flex;
  flex-direction: column;
  @media ${mediaQueries.medium} {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px;
  }
  @media ${mediaQueries.large} {
    border-radius: 6px;
    box-shadow: 0 1px 0 0 rgb(0 0 0 / 25%);
  }
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media ${mediaQueries.medium} {
    flex-direction: row;
  }
`

const LeftWrapper = styled.div`
  padding: 24px 16px 0px;
  border-radius: 16px 16px 0px 0px;
  @media ${mediaQueries.medium} {
    margin-bottom: 0px;
    padding: 0px 0px;
    max-width: 70%;
  }
`

const PaddingWrapper = styled.div`
  padding: 0px 16px 16px;
  @media ${mediaQueries.medium} {
    padding: 0px;
  }
`

const BorderWrapper = styled.div`
  @media ${mediaQueries.medium} {
    border-left: 1px solid ${colors.grey300};
  }
`

const RatingReviewDivider = styled(Divider)`
  margin: 0px 16px;
  width: auto;
`

const RowWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 20px;
  cursor: default;
`

const BadgeSection = styled.div`
  display: flex;
  flex-flow: wrap;
  margin-top: 14px;
  @media ${mediaQueries.medium} {
    margin: 10px 0px 0px 74px;
  }
`

const BadgeLabel = styled(TextCallout1)`
  margin: 0px 4px 0px 8px;
`

const Description = styled(TextCallout2)`
  text-overflow: ellipsis;
  overflow: hidden;
  margin-top: 14px;
  @media ${mediaQueries.medium} {
    margin: 14px 0px 0px 74px;
  }
`
const Button = styled.button`
  width: auto;
  display: flex;
  align-items: center;

  margin-top: 12px;
  margin-bottom: 18px;
  cursor: pointer;

  @media ${mediaQueries.medium} {
    margin-left: 74px;
    margin-bottom: 0px;
  }
`

const TextButton = styled(TextCaption2)`
  text-decoration: underline;
  margin-left: 8px;
`

const UserInfo = ({
  profileData: { account = {}, profileInfo = {}, badges = {}, co2Savings = {} },
  userSdrn,
  apiUrl,
  loadTrustSettings,
  isMobile,
}) => {
  const [popoverToShow, setPopoverToShow] = useState(null)
  const [showReportUserModal, setShowReportUserModal] = useState(false)

  const bankIdVerificationEnabled = useSelector(
    (state) => state.optimizely.features.bankIdVerification?.enabled,
  )

  const hasBadges = Boolean(badges?.length)
  const co2Data = co2Savings || ''
  const showCo2Data = !profileInfo.hasReviews && !loadTrustSettings && co2Data

  const clickReportUser = () => {
    setShowReportUserModal(true)
  }

  const closeReportUserModal = () => {
    setShowReportUserModal(false)
  }

  const badgesContent = badges?.map((badge) => {
    const iconElements = {
      IconMedalMulticolor: <IconMedalMulticolor width={24} height={24} />,
      IconQuickResponseMulticolor: <IconQuickResponseMulticolor width={24} height={24} />,
      IconLeafMulticolor: <IconLeafMulticolor width={24} height={24} />,
    }
    return (
      <RowWrapper
        key={badge.id}
        onMouseEnter={() => setPopoverToShow(badge.id)}
        onMouseLeave={() => setPopoverToShow(null)}
      >
        {popoverToShow === badge.id && (
          <PopoverCentered heading={badge.label} text={badge.description} distance={40} />
        )}
        {iconElements[badge.iconBlocketUi]}
        <BadgeLabel>{badge.label}</BadgeLabel>
      </RowWrapper>
    )
  })

  return (
    <UserProfileSection data-cy="public-user-profile-info">
      {showReportUserModal && (
        <ReportUser
          closeReportUserModal={closeReportUserModal}
          account={account}
          isMobile={isMobile}
        />
      )}
      <Wrapper>
        <LeftWrapper>
          <ProfileCard
            profileName={profileInfo.name}
            account={account}
            showVerificationInfo={bankIdVerificationEnabled}
          />
          {hasBadges && <BadgeSection>{badgesContent}</BadgeSection>}
          {profileInfo.description && <Description>{profileInfo.description}</Description>}
          <Button onClick={clickReportUser}>
            <IconWarning width={14} height={14} />
            <TextButton>Rapportera användare</TextButton>
          </Button>
        </LeftWrapper>

        {loadTrustSettings ? (
          <>
            {isMobile && <RatingReviewDivider />}
            <trust-settings settings={settings({ apiUrl })}>
              <BorderWrapper>
                <trust-summary user={userSdrn} href="#summary-link-target" small></trust-summary>
              </BorderWrapper>
            </trust-settings>
          </>
        ) : (
          showCo2Data && (
            <PaddingWrapper>
              <SustainabilityInfo data={co2Data} />
            </PaddingWrapper>
          )
        )}
      </Wrapper>
    </UserProfileSection>
  )
}

UserInfo.propTypes = {
  profileData: PropTypes.object.isRequired,
  userSdrn: PropTypes.string,
  apiUrl: PropTypes.string,
  loadTrustSettings: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default UserInfo
